// A thin wrapper over the real Fetch API — no object literals (KopScript
// has no syntax for one), no generics, no automatic JSON deserialization.
// `Response.Text()` gets you the raw body; for a typed JSON response,
// describe the shape as its own `extern class` and parse it with a
// per-shape `extern ... as "JSON.parse"` declaration (see Kopular's README)
// — the same trust-based approach `extern` already uses for everything
// else, not a new mechanism.

extern class Response {
  bool ok { get; }
  number status { get; }
  task<string> text();
};

// GET/HEAD/DELETE-without-a-body need no options object at all, so they
// bind straight to the real global `fetch` — no runtime helper involved.
extern task<Response> FetchUrl(string url) as "fetch";

// POST/PUT/PATCH (and DELETE-with-a-body) need to set a method/body/
// headers, which does need an options object — the one thing in this file
// that isn't a direct, unassisted binding to a real JS global. See
// http_runtime.js for why, and for the only hand-written JS in this
// package. A relative path, not a package-name one: this is Kopular
// referencing its own sibling file (which isn't part of Kopular's public
// API — only Http's static methods below are), not a consumer reaching
// into Kopular from outside.
extern task<Response> RequestWithBody(string url, string method, string? body, string contentType) from "./http_runtime.js" as "requestWithBody";

// Static methods, not free functions, purely so call sites read as
// `Http.Get(url)` / `Http.Post(url, body)` — there's no instance state here
// to justify a real object.
class Http {
  public static async task<Response> Get(string url) {
    return await FetchUrl(url);
  }

  public static async task<Response> Delete(string url) {
    return await RequestWithBody(url, "DELETE", null, "application/json");
  }

  public static async task<Response> Post(string url, string jsonBody) {
    return await RequestWithBody(url, "POST", jsonBody, "application/json");
  }

  public static async task<Response> Put(string url, string jsonBody) {
    return await RequestWithBody(url, "PUT", jsonBody, "application/json");
  }

  public static async task<Response> Patch(string url, string jsonBody) {
    return await RequestWithBody(url, "PATCH", jsonBody, "application/json");
  }
}
